/** SSE client — /api/agui/sse stream → devAgentStore (feed, streaming, pipeline). */
import { useEffect, useRef, useCallback } from "react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { stopDevAgentExecution } from "../devAgentService";
import { sseState } from "./sseMutableState";
import { S } from "./sseFeedHelpers";
import {
  buildDevAgentSseRequest,
  resetSseRuntimeForConnect,
  resetSseRuntimeForSession,
  teardownSseRuntime,
} from "./sseRuntime";
import { refreshSseTicketWithRecovery } from "./sseAuth";
import { processSseFrame } from "./sseStream";

const activeSseConnections = new Map<
  string,
  { controller: AbortController; owner: symbol }
>();

export function useDevAgentSSE(uuid: string | null) {
  const abortRef = useRef<AbortController | null>(null);
  const ownerRef = useRef(Symbol("dev-agent-sse"));
  const retryRef = useRef(0);
  const lastEventIdRef = useRef("0");
  const processedIdsRef = useRef(new Set<string>());
  const connectedUuidRef = useRef<string | null>(null);

  const connectTo = useCallback((targetUuid: string) => {
    const sid = String(targetUuid ?? "").trim();
    if (!sid) {
      console.warn("[useDevAgentSSE] connectTo skipped: empty or null session uuid");
      return;
    }
    const isStale = Date.now() - sseState.lastEventAt > 120_000;
    const existing = activeSseConnections.get(sid);
    if (existing && !existing.controller.signal.aborted && !isStale) {
      connectedUuidRef.current = sid;
      return;
    }
    if (existing) {
      existing.controller.abort();
      activeSseConnections.delete(sid);
    }

    if (
      connectedUuidRef.current === sid &&
      abortRef.current &&
      !abortRef.current.signal.aborted &&
      !isStale
    ) {
      return;
    }
    abortRef.current?.abort();
    connectedUuidRef.current = sid;
    resetSseRuntimeForConnect(processedIdsRef.current);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    activeSseConnections.set(sid, { controller: ctrl, owner: ownerRef.current });
    const lastId = lastEventIdRef.current || "0";
    const request = buildDevAgentSseRequest({
      sessionUuid: sid,
      lastEventId: lastId,
      sseTicket: useDevAgentStore.getState().sseTicket,
    });
    if (!request.url) {
      activeSseConnections.delete(sid);
      abortRef.current = null;
      connectedUuidRef.current = null;
      return;
    }

    (async () => {
      try {
        const res = await fetch(request.url, {
          signal: ctrl.signal,
          headers: request.headers,
        });
        if (!res.ok || !res.body) {
          if (res.status === 401 && retryRef.current === 0) {
            console.warn("[SSE] 401 — refreshing ticket");
            retryRef.current = 1;
            try {
              const newTicket = await refreshSseTicketWithRecovery(sid);
              if (newTicket) {
                useDevAgentStore.getState().setSseTicket(newTicket);
                connectedUuidRef.current = null;
                connectTo(sid);
                return;
              }
            } catch {
              /* fall through to user-visible failure */
            }
          }
          S().setConnectionStatus("disconnected");
          if (res.status === 401) {
            console.warn("[SSE] 401 — ticket invalid or expired for this chat session");
            import("react-hot-toast").then(({ default: t }) =>
              t.error(
                "Live stream could not authorize. Use Retry in the header or switch session again.",
                { duration: 6000 },
              ));
          }
          return;
        }
        retryRef.current = 0;
        S().setConnectionStatus("connected");
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        let eventId = "";
        let eventType = "message";
        let eventData = "";
        let shouldClose = false;
        let shouldReconnect = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const l of lines) {
            if (l.startsWith("id: ")) {
              eventId = l.slice(4).trim();
              continue;
            }
            if (l.startsWith("event: ")) {
              eventType = l.slice(7).trim();
              continue;
            }
            if (l.startsWith("data: ")) {
              eventData += l.slice(6);
              continue;
            }
            if (l.trim() !== "" || !eventData) continue;
            const action = await processSseFrame({
              targetUuid: sid,
              eventId,
              eventType,
              eventData,
              processedIdsRef,
              lastEventIdRef,
              connectedUuidRef,
              refreshTicketWithRecovery: refreshSseTicketWithRecovery,
              reconnect: connectTo,
            });
            eventType = "message";
            eventData = "";
            eventId = "";
            if (action === "close") {
              shouldClose = true;
              break;
            }
            if (action === "reconnect") {
              shouldReconnect = true;
              break;
            }
          }
          if (shouldClose || shouldReconnect) break;
        }

        if (shouldClose) {
          try {
            await reader.cancel();
          } catch {
            /* ignore */
          }
          if (abortRef.current === ctrl) {
            abortRef.current = null;
          }
          if (activeSseConnections.get(sid)?.controller === ctrl) {
            activeSseConnections.delete(sid);
          }
          return;
        }

        if (shouldReconnect) {
          try {
            await reader.cancel();
          } catch {
            /* ignore */
          }
          if (abortRef.current === ctrl) {
            abortRef.current = null;
          }
          if (activeSseConnections.get(sid)?.controller === ctrl) {
            activeSseConnections.delete(sid);
          }
          return;
        }
        if (activeSseConnections.get(sid)?.controller === ctrl) {
          activeSseConnections.delete(sid);
        }
        if (
          useDevAgentStore.getState().sessionUuid === sid &&
          S().runStatus === "running"
        ) {
          scheduleReconnect();
        }
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        if (activeSseConnections.get(sid)?.controller === ctrl) {
          activeSseConnections.delete(sid);
        }
        console.warn("[SSE] fetch error:", e?.message || String(e));
        if (
          useDevAgentStore.getState().sessionUuid === sid &&
          S().runStatus === "running"
        ) {
          scheduleReconnect();
        }
      }
    })();

    function scheduleReconnect() {
      if (ctrl.signal.aborted) return;
      const attempt = retryRef.current++;
      if (attempt >= 10) {
        S().setConnectionStatus("disconnected");
        S().setRunStatus("idle");
        return;
      }
      S().setConnectionStatus("reconnecting");
      const delay = Math.min(1000 * 2 ** attempt, 15000);
      setTimeout(() => {
        if (ctrl.signal.aborted) return;
        if (useDevAgentStore.getState().sessionUuid !== sid) return;
        const reconnectUuid = connectedUuidRef.current || sid;
        if (!reconnectUuid) return;
        connectedUuidRef.current = null;
        connectTo(reconnectUuid);
      }, delay);
    }
  }, []);

  // Disconnect from the current SSE stream WITHOUT sending /stop to the
  // backend. Use this when navigating away from a session — the agent run
  // should keep going server-side; we just stop listening locally.
  const disconnect = useCallback(() => {
    const u = connectedUuidRef.current;
    const hasLocalConnection = Boolean(abortRef.current);
    let ownsActiveConnection = false;
    if (u) {
      const active = activeSseConnections.get(u);
      if (active?.owner === ownerRef.current) {
        ownsActiveConnection = true;
        active.controller.abort();
        activeSseConnections.delete(u);
      }
    }
    abortRef.current?.abort();
    connectedUuidRef.current = null;
    if (!ownsActiveConnection && !hasLocalConnection) {
      return;
    }
    teardownSseRuntime({ processedIds: processedIdsRef.current });
  }, []);

  // Stop the running agent — sends POST /stop AND disconnects locally.
  // Use this only for explicit user-initiated stop (Stop button, Escape key).
  // Do NOT call this on session navigation — use disconnect() instead.
  const stop = useCallback(() => {
    const u = connectedUuidRef.current;
    if (u) stopDevAgentExecution(u).catch(() => {});
    // teardownSseRuntime({ processedIds: processedIdsRef.current, setRunIdle: true });
    S().setRunStatus("idle");
  }, []);

  useEffect(() => {
    resetSseRuntimeForSession(processedIdsRef.current);
    // Don't reset lastEventIdRef — on session switch-back, SSE replays
    // from where we left off so no events are lost mid-stream.
    const u = uuid?.trim();
    if (u) connectTo(u);

    return () => {
      // Abort the SSE connection on unmount / uuid change so orphaned
      // readers don't keep appending deltas to the store (causes doubled text).
      abortRef.current?.abort();
      connectedUuidRef.current = null;
    };
  }, [uuid, connectTo]);

  const forceReconnect = useCallback(
    (targetUuid: string) => {
      const sid = String(targetUuid ?? "").trim();
      if (!sid) {
        console.warn("[useDevAgentSSE] forceReconnect skipped: empty session uuid");
        return;
      }
      abortRef.current?.abort();
      connectedUuidRef.current = null;
      connectTo(sid);
    },
    [connectTo],
  );

  return { stop, disconnect, connectTo, forceReconnect };
}
